import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { Calendar, CheckCircle2 } from "lucide-react";
import { useBookingQuery } from "@/hooks/useBookingQuery";
import { utcDayKey } from "@/lib/calendarDay";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";

type ConfirmationProps = {
  onClose?: () => void;
  triggerRefresh?: () => void;
};

const Confirmation = ({ onClose, triggerRefresh }: ConfirmationProps) => {
  const navigate = useNavigate();
  const { booking, changingWhat, resetFlow } = useChangeBookingFlow();

  // Re-fetch the booking so the calendar link reflects the CHANGE just made (new date/room), not the
  // stale pre-edit values on the flow's booking object. Mirrors the create flow's Done screen.
  const { data: freshBooking } = useBookingQuery(booking?.id ?? "", { enabled: !!booking?.id });
  const calendarBooking = freshBooking ?? booking;

  // Trigger refresh when confirmation screen is shown
  useEffect(() => {
    if (triggerRefresh) {
      triggerRefresh();
    }
  }, [triggerRefresh]);

  const handleClose = () => {
    resetFlow();
    onClose?.();
  };

  const handleViewOnCalendar = () => {
    if (!calendarBooking?.location?.slug || !calendarBooking.date) return;
    if (triggerRefresh) triggerRefresh();
    const dateStr = utcDayKey(new Date(calendarBooking.date));
    navigate(`/location/${calendarBooking.location.slug}/calendar?date=${dateStr}`);
    resetFlow();
    onClose?.();
  };

  const getConfirmationMessage = () => {
    switch (changingWhat) {
      case "datetime":
        return {
          title: "Date & Time Changed Successfully",
          message: "Your booking date and time have been updated.",
          details: "",
        };
      case "duration":
        return {
          title: "Duration Changed Successfully",
          message: "Your booking duration has been updated.",
          details: "",
        };
      case "room":
        return {
          title: "Room Changed Successfully",
          message: "Your booking room has been updated.",
          details: "",
        };
      default:
        return {
          title: "Change Completed Successfully",
          message: "Your booking has been updated.",
          details: "",
        };
    }
  };

  const confirmation = getConfirmationMessage();

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-green-50">
        <h2 className="text-2xl font-semibold mb-2 text-black">Success!</h2>
        <p className="text-green-700 text-xs">Your booking change has been processed.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-green-50">
          <h2 className="text-3xl font-semibold mb-4 text-black">Success!</h2>
          <p className="mb-2 text-black text-sm">Your booking change has been completed successfully.</p>
          {booking && (
            <div className="mt-4 space-y-2">
              <div className="p-3 bg-white rounded border border-gray-300 shadow">
                <p className="text-xs font-semibold text-gray-700">Booking Reference:</p>
                <p className="text-sm text-gray-600 mt-1 uppercase">{booking.shortCode}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Content */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
            <CheckCircle2 className="size-24 text-green-600" />

            <div className="text-center space-y-3">
              <h3 className="text-2xl font-semibold text-gray-900">{confirmation.title}</h3>
              <p className="text-lg text-gray-700">{confirmation.message}</p>
              <p className="text-sm text-gray-600 max-w-md">{confirmation.details}</p>
            </div>

            {/* {(changingWhat === "datetime" || changingWhat === "duration" || changingWhat === "room") && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
                <p className="text-sm text-blue-900 text-center">
                  You can view your updated booking details in your bookings list or in the confirmation email.
                </p>
              </div>
            )} */}
          </div>

          <div className="flex flex-col gap-2 mt-6">
            <Button
              variant="outline"
              className={`w-full ${secondaryButtonClasses}`}
              onClick={handleViewOnCalendar}
              disabled={!calendarBooking?.location?.slug}
            >
              <Calendar className="mr-2 h-4 w-4" />
              View on Calendar
            </Button>
            <Button className={`w-full ${defaultButtonClasses}`} onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
          <CheckCircle2 className="size-24 text-green-600" />

          <div className="text-center space-y-3">
            <h3 className="text-2xl font-semibold text-gray-900">{confirmation.title}</h3>
            <p className="text-lg text-gray-700">{confirmation.message}</p>
            <p className="text-sm text-gray-600">{confirmation.details}</p>
          </div>

          {/* Additional information based on change type */}
          {/* {(changingWhat === "datetime" || changingWhat === "duration" || changingWhat === "room") && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900 text-center">
                You can view your updated booking details in your bookings list or in the confirmation email.
              </p>
            </div>
          )} */}
        </div>

        <div className="flex flex-col gap-2 mt-6">
          <Button
            variant="outline"
            className={`w-full ${secondaryButtonClasses}`}
            onClick={handleViewOnCalendar}
            disabled={!calendarBooking?.location?.slug}
          >
            <Calendar className="mr-2 h-4 w-4" />
            View on Calendar
          </Button>
          <Button className={`w-full ${defaultButtonClasses}`} onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
